import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  ArrowRight,
  Phone,
  XCircle,
  CheckCircle2,
  Building2,
  ShieldCheck,
  BadgeCheck,
} from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import Breadcrumbs from "./Breadcrumbs";
import SEO from "./SEO";
import CalculatorCallout from "./CalculatorCallout";
import { carriers } from "../data/carriers";
import { businessInfo } from "../mock";
import { localBusinessSchema, faqSchema, breadcrumbSchema } from "../utils/schemas";

const PILLAR = "/florida-insurance-carrier-claim-help";

const CarrierPage = () => {
  const { slug } = useParams();
  const page = carriers[slug];
  if (!page) return <Navigate to="/" replace />;

  const siblings = Object.values(carriers).filter(
    (p) => p.slug !== slug && !p.isPillar
  );

  const crumbTrail = page.isPillar
    ? [{ name: "Insurance Carrier Claim Help", path: PILLAR }]
    : [
        { name: "Insurance Carrier Claim Help", path: PILLAR },
        { name: page.carrier, path: `/${page.slug}` },
      ];

  const schemas = [
    localBusinessSchema(),
    faqSchema(page.faqs),
    breadcrumbSchema([{ name: "Home", path: "/" }, ...crumbTrail]),
  ];

  return (
    <div className="bg-black min-h-screen text-white">
      <SEO
        title={page.metaTitle}
        description={page.metaDescription}
        path={`/${page.slug}`}
        keywords={page.keywords}
        schemas={schemas}
      />
      <Header />
      <Breadcrumbs items={crumbTrail} />

      {/* Hero */}
      <section className="bg-black border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 md:py-20">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="h-4 w-4 text-[#E11D26]" />
            <span className="text-[#E11D26] text-xs font-bold tracking-[0.25em] uppercase">
              Carrier Claim Help · {page.topic}
            </span>
          </div>
          <h1
            className="text-white text-3xl sm:text-4xl md:text-5xl font-black uppercase leading-[1.02]"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            {page.h1}
          </h1>
          <p className="mt-6 text-white/80 text-base sm:text-lg leading-relaxed">
            {page.intro}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to="/#contact"
              data-testid="carrier-cta-review"
              className="group inline-flex items-center justify-center gap-3 bg-[#E11D26] hover:bg-[#c11820] text-white font-bold uppercase tracking-wider px-7 py-4 transition-colors"
            >
              <span className="text-sm">Get My Free Claim Review</span>
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href={`tel:${businessInfo.phoneRaw}`}
              className="inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-white/90 font-bold uppercase tracking-wider px-7 py-4 transition-colors"
            >
              <Phone className="h-4 w-4" />
              <span className="text-sm">{businessInfo.phone}</span>
            </a>
          </div>
        </div>
      </section>

      {/* What we do on the claim */}
      <section className="py-14 md:py-16 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2
            className="text-2xl sm:text-3xl font-black text-white uppercase leading-tight"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            How We Fight for Your {page.topic} Payout
          </h2>
          <ul className="mt-7 grid sm:grid-cols-2 gap-3">
            {page.points.map((p, i) => (
              <li
                key={i}
                className="flex items-start gap-3 bg-[#0F0F0F] border border-white/10 p-4"
              >
                <CheckCircle2 className="h-5 w-5 text-[#E11D26] shrink-0 mt-0.5" />
                <span className="text-white/85 text-sm leading-relaxed">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Why claims get underpaid / denied */}
      <section className="py-14 md:py-16 bg-black border-y border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2
            className="text-2xl sm:text-3xl font-black text-white uppercase leading-tight"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Why {page.topic} Claims Get Underpaid or Denied
          </h2>
          <ul className="mt-7 space-y-3">
            {page.denialReasons.map((r, i) => (
              <li
                key={i}
                className="flex items-start gap-3 bg-[#0F0F0F] border border-white/10 p-4"
              >
                <XCircle className="h-5 w-5 text-[#E11D26] shrink-0 mt-0.5" />
                <span className="text-white/85 text-sm leading-relaxed">{r}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {!page.isPillar && (
              <Link
                to={PILLAR}
                data-testid="carrier-pillar-link"
                className="inline-flex items-center gap-2 bg-[#E11D26]/10 border border-[#E11D26]/50 hover:bg-[#E11D26] text-white px-5 py-3 text-sm font-bold tracking-wider uppercase transition-colors"
              >
                <Building2 className="h-4 w-4" /> All Carrier Claim Help
              </Link>
            )}
            <Link
              to="/denied-insurance-claim-florida"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-[#E11D26] text-white px-5 py-3 text-sm font-bold tracking-wider uppercase transition-colors"
            >
              Denied Claims <ArrowRight className="h-4 w-4 text-[#E11D26]" />
            </Link>
            <Link
              to="/underpaid-insurance-claim-florida"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-[#E11D26] text-white px-5 py-3 text-sm font-bold tracking-wider uppercase transition-colors"
            >
              Underpaid Claims <ArrowRight className="h-4 w-4 text-[#E11D26]" />
            </Link>
          </div>
        </div>
      </section>

      {/* Calculator callout */}
      <CalculatorCallout
        eyebrow={`Before You Accept ${page.topic}'s Offer`}
        headline="See What Your Claim Should Actually Pay."
        body="Run your damage through our free calculator to get a defensible recovery range built from current Florida market rates — then we'll follow up to fight for every dollar."
        ctaLabel="Calculate My Claim"
        source={`carrier-${page.slug}`}
      />

      {/* FAQ */}
      <section className="py-14 md:py-16 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2
            className="text-2xl sm:text-3xl font-black text-white uppercase"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            {page.topic} Claim FAQ
          </h2>
          <div className="mt-8 divide-y divide-white/10 border-y border-white/10">
            {page.faqs.map((f, i) => (
              <details key={i} className="group py-5" data-testid={`carrier-faq-${i}`}>
                <summary className="flex justify-between items-start cursor-pointer list-none gap-6">
                  <h3 className="text-white text-base sm:text-lg font-bold uppercase tracking-wide leading-snug">
                    {f.question}
                  </h3>
                  <span className="text-[#E11D26] text-xl group-open:rotate-45 transition-transform shrink-0">
                    +
                  </span>
                </summary>
                <p className="mt-4 text-white/75 leading-relaxed text-sm sm:text-base">
                  {f.answer}
                </p>
              </details>
            ))}
          </div>
          <p className="mt-6 text-white/45 text-xs italic">
            Carrier names are used for identification only; Claim Beast is an
            independent public adjusting firm and is not affiliated with or
            endorsed by any insurance company. This information is general and
            educational, not legal advice. Coverage, deadlines, and provisions
            vary by policy — review your policy or consult a professional.
          </p>
        </div>
      </section>

      {/* E-E-A-T: expert-reviewed trust block */}
      <section className="py-12 bg-black border-y border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex items-start gap-4 bg-[#0F0F0F] border-l-4 border-[#E11D26] p-6">
            <BadgeCheck className="h-8 w-8 text-[#E11D26] shrink-0 mt-0.5" />
            <div>
              <div className="text-[#E11D26] text-xs font-bold tracking-[0.25em] uppercase">
                Reviewed by a Licensed Florida Public Adjuster
              </div>
              <p className="mt-2 text-white/80 text-sm leading-relaxed">
                This page is maintained by the team at Claim Beast Public
                Adjusters, led by Joe — a Florida &amp; Georgia licensed public
                adjuster (FL License #{businessInfo.licenses.florida}) with over
                a decade of insurance-industry experience representing Tampa Bay
                homeowners.{" "}
                <Link
                  to="/about"
                  className="text-[#E11D26] hover:text-white underline underline-offset-2 font-semibold"
                >
                  Meet the Beasts
                </Link>
                .
              </p>
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-white/50 text-xs">
                <span className="flex items-center gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5 text-[#E11D26]" /> Firm
                  License {businessInfo.licenses.firm}
                </span>
                <span>FL License {businessInfo.licenses.florida}</span>
                <span>GA License {businessInfo.licenses.georgia}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cluster nav — other carriers */}
      <section className="py-14 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-6">
            <Building2 className="h-5 w-5 text-[#E11D26]" />
            <h2
              className="text-2xl sm:text-3xl font-black text-white uppercase"
              style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
            >
              {page.isPillar ? "Claim Help by Carrier" : "Other Carriers We Handle"}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {siblings.map((s) => (
              <Link
                key={s.slug}
                to={`/${s.slug}`}
                data-testid={`carrier-cluster-${s.slug}`}
                className="group flex items-center justify-between gap-4 bg-[#0F0F0F] border border-white/10 hover:border-[#E11D26] hover:bg-[#1a0709] p-5 transition-colors"
              >
                <span className="text-white font-bold text-sm uppercase tracking-wide">
                  {s.topic}
                </span>
                <ArrowRight className="h-4 w-4 text-[#E11D26] transition-transform group-hover:translate-x-1" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 bg-[#E11D26]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3
              className="text-white text-2xl sm:text-3xl md:text-4xl font-black uppercase leading-tight"
              style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
            >
              Don&apos;t accept the first number.
            </h3>
            <p className="mt-2 text-white/90 text-sm sm:text-base">
              Free claim review — no upfront cost, no obligation.
            </p>
          </div>
          <a
            href={`tel:${businessInfo.phoneRaw}`}
            className="inline-flex items-center justify-center gap-2 bg-black hover:bg-[#0A0A0A] text-white font-bold uppercase tracking-wider px-7 py-4 transition-colors shrink-0"
          >
            <Phone className="h-4 w-4" /> {businessInfo.phone}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CarrierPage;
